/**
 * Known merchant HQ geocoder.
 *
 * Maps the top card-spend merchants to their actual headquarters so arcs
 * land at the real location instead of a generic country centroid (NYC for
 * all "USA" merchants). This spreads the US destinations across the West
 * Coast and makes the map geographically honest.
 *
 * Only the top ~25 merchants by volume are here; unknown merchants fall
 * back to the country-based geocoder.
 */

import type { GeoCity } from "./geocode";

type MerchantEntry = GeoCity & {
  /** Short display name for the landing label (trimmed from raw merchant_name). */
  displayName: string;
};

const MERCHANT_HQ: Record<string, MerchantEntry> = {
  // --- Social / Ads ---
  Meta: {
    lat: 37.4529, lng: -122.1817,
    city: "Menlo Park", country: "US",
    displayName: "Meta",
  },
  TikTok: {
    lat: 1.3521, lng: 103.8198,
    city: "Singapore", country: "SG",
    displayName: "TikTok",
  },
  // --- Tech / Cloud ---
  Google: {
    lat: 37.3861, lng: -122.0839,
    city: "Mountain View", country: "US",
    displayName: "Google",
  },
  "Google Ads": {
    lat: 37.3861, lng: -122.0839,
    city: "Mountain View", country: "US",
    displayName: "Google Ads",
  },
  Apple: {
    lat: 37.323, lng: -122.0322,
    city: "Cupertino", country: "US",
    displayName: "Apple",
  },
  "Claude.ai": {
    lat: 37.7749, lng: -122.4194,
    city: "San Francisco", country: "US",
    displayName: "Claude",
  },
  // --- Payments ---
  PayPal: {
    lat: 37.3382, lng: -121.8863,
    city: "San Jose", country: "US",
    displayName: "PayPal",
  },
  // --- Commerce ---
  Alibaba: {
    lat: 30.2741, lng: 120.1551,
    city: "Hangzhou", country: "CN",
    displayName: "Alibaba",
  },
  AliExpress: {
    lat: 30.2741, lng: 120.1551,
    city: "Hangzhou", country: "CN",
    displayName: "AliExpress",
  },
  // --- Connectivity ---
  Starlink: {
    lat: 30.0, lng: -95.5,
    city: "Houston", country: "US",
    displayName: "Starlink",
  },
  "Lyca Mobile": {
    lat: 51.5074, lng: -0.1278,
    city: "London", country: "GB",
    displayName: "Lyca",
  },
  // --- Hosting / Dev ---
  Hostinger: {
    lat: 54.6872, lng: 25.2797,
    city: "Vilnius", country: "LT",
    displayName: "Hostinger",
  },
  Namecheap: {
    lat: 33.4484, lng: -112.074,
    city: "Phoenix", country: "US",
    displayName: "Namecheap",
  },
  // --- Messaging ---
  Telegram: {
    lat: 25.2048, lng: 55.2708,
    city: "Dubai", country: "AE",
    displayName: "Telegram",
  },
  // --- Gaming ---
  Konami: {
    lat: 35.6762, lng: 139.6503,
    city: "Tokyo", country: "JP",
    displayName: "Konami",
  },
  PlayStation: {
    lat: 37.3861, lng: -122.0839,
    city: "San Mateo", country: "US",
    displayName: "PlayStation",
  },
  // --- Entertainment ---
  Netflix: {
    lat: 37.2358, lng: -121.9624,
    city: "Los Gatos", country: "US",
    displayName: "Netflix",
  },
  Spotify: {
    lat: 59.3293, lng: 18.0686,
    city: "Stockholm", country: "SE",
    displayName: "Spotify",
  },
  // --- Other notable ---
  Eni: {
    lat: 45.4642, lng: 9.19,
    city: "Milan", country: "IT",
    displayName: "Eni",
  },
};

/**
 * Resolve a merchant name to its HQ location + clean display name.
 * Returns null if the merchant isn't in our lookup (caller falls back to
 * country-based geocoding).
 */
export function resolveMerchantHQ(
  merchantName: string | null | undefined,
): MerchantEntry | null {
  if (!merchantName) return null;
  // Exact match first.
  const exact = MERCHANT_HQ[merchantName.trim()];
  if (exact) return exact;
  // Fuzzy: check if any key is a prefix of the merchant name.
  const lower = merchantName.trim().toLowerCase();
  for (const [key, entry] of Object.entries(MERCHANT_HQ)) {
    if (lower.startsWith(key.toLowerCase())) return entry;
  }
  return null;
}

/**
 * Clean up a raw merchant name for display on the map.
 * If known, uses the short displayName. Otherwise truncates.
 */
export function merchantLabel(
  merchantName: string | null | undefined,
): string | null {
  if (!merchantName) return null;
  const known = resolveMerchantHQ(merchantName);
  if (known) return known.displayName;
  // Unknown merchant: truncate to 12 chars for the landing label.
  const clean = merchantName.trim();
  return clean.length > 12 ? clean.slice(0, 11) + "…" : clean;
}
